import React, { forwardRef } from 'react';
import { DefaultProps, MantineColor, ClassNames, useExtractedMargins } from '@mantine/styles';
import { CloseButton } from '../ActionIcon';
import useStyles from './Alert.styles';

export type AlertStylesNames = ClassNames<typeof useStyles>;

export interface AlertProps
  extends DefaultProps<AlertStylesNames>,
    Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  /** Alert title */
  title?: React.ReactNode;

  /** Alert message */
  children: React.ReactNode;

  /** Color from theme.colors */
  color?: MantineColor;

  /** Icon displayed next to title */
  icon?: React.ReactNode;

  /** True to display close button */
  withCloseButton?: boolean;

  /** Called when close button is clicked */
  onClose?(): void;

  /** Close button aria-label */
  closeButtonLabel?: string;
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      title,
      children,
      color,
      style,
      classNames,
      icon,
      styles,
      sx,
      onClose,
      withCloseButton,
      ...others
    }: AlertProps,
    ref
  ) => {
    const { classes, cx } = useStyles({ color }, { classNames, styles, sx, name: 'Alert' });
    const { mergedStyles, rest } = useExtractedMargins({ others, style });

    return (
      <div className={cx(classes.root, className)} style={mergedStyles} ref={ref} {...rest}>
        <div className={classes.wrapper}>
          {icon && <div className={classes.icon}>{icon}</div>}

          <div className={classes.body}>
            {title && (
              <div className={classes.title}>
                <span className={classes.label}>{title}</span>

                {withCloseButton && (
                  <CloseButton
                    className={classes.closeButton}
                    onClick={() => onClose?.()}
                    variant="transparent"
                    size={16}
                    iconSize={16}
                  />
                )}
              </div>
            )}

            <div className={classes.message}>{children}</div>
          </div>
        </div>
      </div>
    );
  }
);

Alert.displayName = '@mantine/core/Alert';
